import React, { useState } from 'react';
import { FormControl, FormLabel, InputLabel, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import ShowContainer from './ShowContainer';
import CustomSelect from '../forms/theme-elements/CustomSelect';
import { useAppSelector } from '../../store';
import CusTooltip from './CusTooltip';

export default function CusLabelSelect({
  id,
  label,
  options = [],
  value,
  fullWidth = true,
  /**
   * @deprecated toolTipTitle - use toolTipText
   */
  toolTipTitle = '',
  toolTipText = '',
  isRow,
  error,
  multi,
  width,
  marginRight,
  selectProps = {},
  size = undefined,
  isVerySmall,
  isTranslateOptions = true,
  colorsOptions = [],
  readOnly,
  disabled,
  tooltipBgColor,
  tooltipLabelText,
  onChange = (value) => {}
}) {
  const { t } = useTranslation();
  const defaultInputSize = useAppSelector((state) => state.customizer.inputSize);

  const onMenuClick = (el) => {
    const elemValue = el.value;
    if (!multi) {
      onChange(elemValue);
      return;
    }

    const isChecked = value.includes(elemValue);
    const newValue = isChecked ? value.filter((v) => v !== elemValue) : [...value, elemValue];

    onChange(newValue);
  };

  if (isVerySmall) {
    selectProps['slotProps'] = { input: { sx: { paddingY: 0.5 } } };
  }

  const Placeholder = () => <span className="placeholder">{t('Select')}</span>;
  const selectValue = multi ? value : options.some((opt) => opt.value === value) ? value : '';
  const myT = (label) => (typeof label == 'string' && isTranslateOptions ? t(label) : label);

  return (
    <Grid size={size} width={width}>
      <FormControl fullWidth={fullWidth}>
        <Stack
          display={'flex'}
          flexDirection={isRow ? 'row' : 'column'}
          alignItems={isRow ? 'center' : undefined}
          marginRight={marginRight}
          gap={isRow ? 0.75 : isVerySmall ? 0 : 0.5}>
          <ShowContainer condition={label}>
            <Stack direction={'row'} alignItems={'center'} gap={0.5} flexWrap={'wrap-reverse'}>
              <Typography
                color={disabled ? 'textDisabled' : 'textSecondary'}
                fontSize={isVerySmall ? 12 : undefined}>
                {typeof label == 'string' ? t(label) : label}
              </Typography>
              {tooltipLabelText && <CusTooltip text={tooltipLabelText} />}
            </Stack>
          </ShowContainer>
          <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
            {options && options.length ? (
              <CustomSelect
                id={id}
                readOnly={readOnly}
                name={id}
                variant="outlined"
                fullWidth
                disabled={disabled}
                value={selectValue}
                displayEmpty
                size={defaultInputSize}
                {...selectProps}
                renderValue={(selected) => {
                  const selectLabel = options.find((o) => o.value == selected)?.label;

                  if (multi) {
                    const values = options.map((el) => el.value);
                    const myValue = value.filter((el) => values.includes(el));
                    const myLabel = myValue.map((el) =>
                      myT(options.find((o) => o.value === el)?.label)
                    );
                    return myLabel.join(', ') || <Placeholder />;
                  }

                  const valueRes = options.map((el) => el.value).includes(selected) ? (
                    myT(selectLabel)
                  ) : (
                    <Placeholder />
                  );

                  return valueRes;
                }}
                multiple={multi}>
                {options.map((el, indx) => (
                  <MenuItem
                    key={el.value || indx}
                    value={el.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuClick(el);
                    }}
                    sx={
                      colorsOptions[el.value]
                        ? {
                            background: colorsOptions[el.value] + '!important',
                            '&.Mui-selected': { border: '1px solid' }
                          }
                        : {}
                    }>
                    {typeof el.label == 'string' ? myT(el.label) : el.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            ) : (
              <Typography variant="subtitle2" color="textDisabled">
                {readOnly ? t('not specified') : t('no values')}
              </Typography>
            )}
            <ShowContainer condition={toolTipText || toolTipTitle}>
              <CusTooltip text={toolTipText || toolTipTitle} bgColor={tooltipBgColor} />
            </ShowContainer>
          </Stack>
        </Stack>

        <ShowContainer condition={error}>
          <Stack mt={0.5}>
            <Typography color="error" fontWeight={900}>
              {t(error)}
            </Typography>
          </Stack>
        </ShowContainer>
      </FormControl>
    </Grid>
  );
}
